import { ButtonClean, ButtonDanger } from 'front-end/src/components'
import React, { useState } from 'react'
import { t, Trans } from '@lingui/macro'

import { Modal } from '../../../../components/common/modal/Modal'
import { notification } from '../../../../components/common/notifications/Notifications'
import { ICampaignActionToTriggerChange } from '../../campaign-actions/CampaignActions'
import { translateActionType } from '../create-campaign-action-modal/CreateCampaignActionModal'
import { getCampaignActionName } from 'front-end/src/pages/manage-campaign-page/common'
import {
  useCopyCampaignBannerMutation,
  useCopyCampaignEmailMutation,
  useCopyCampaignMessageMutation,
  useCopyCertificationShareActionMutation,
  useCopyPathwayShareActionMutation,
  useCopySurveyShareActionMutation,
  useCopyTopicShareActionMutation
} from 'front-end/src/pages/manage-campaign-page/campaign-overview/copy-campaign-action-modal/copyCampaignMessage.codegen'
import { CampaignActionTypeEnum } from 'shared/enums/campaigns'

export interface ICopyCampaignActionModalProps {
  campaignId: number
  itemToCopy: ICampaignActionToTriggerChange | null
  onHide: () => void
  onHidden?: () => void
  onCopy: () => Promise<void>
}

export default (props: ICopyCampaignActionModalProps) => {
  const [isCopying, setIsCopying] = useState<boolean>(false)

  const { onHide, onHidden } = props
  const type = `${translateActionType(
    props.itemToCopy?.actionType as CampaignActionTypeEnum
  )}`

  if (!props.itemToCopy?.action) {
    return null
  }

  const name = getCampaignActionName(
    props.itemToCopy.action,
    props.itemToCopy?.actionType
  )

  const [copyCampaignMessage] = useCopyCampaignMessageMutation()
  const [copyTopicShareAction] = useCopyTopicShareActionMutation()
  const [copySurveyShareAction] = useCopySurveyShareActionMutation()
  const [copyPathwayShareAction] = useCopyPathwayShareActionMutation()
  const [copyCampaignEmail] = useCopyCampaignEmailMutation()
  const [copyCampaignBanner] = useCopyCampaignBannerMutation()
  const [copyCertificationShareAction] =
    useCopyCertificationShareActionMutation()

  const copyActionMapper = {
    [CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION]:
      copyCertificationShareAction,
    [CampaignActionTypeEnum.TOPIC_SHARE_ACTION]: copyTopicShareAction,
    [CampaignActionTypeEnum.BANNER]: copyCampaignBanner,
    [CampaignActionTypeEnum.SURVEY_SHARE_ACTION]: copySurveyShareAction,
    [CampaignActionTypeEnum.EMAIL]: copyCampaignEmail,
    [CampaignActionTypeEnum.MESSAGE]: copyCampaignMessage,
    [CampaignActionTypeEnum.PATHWAY_SHARE_ACTION]: copyPathwayShareAction,
    [CampaignActionTypeEnum.MANAGER_NOTIFICATION]: copyCampaignEmail
  }

  const onCopy = async () => {
    setIsCopying(true)
    if (!props.itemToCopy?.actionType) {
      notification.error(
        t`Error while copying campaign action. Unknown type of action`
      )
      return
    }

    const copyHandler = copyActionMapper[props.itemToCopy.actionType]

    await copyHandler({
      variables: {
        campaignId: props.campaignId,
        id: props.itemToCopy?.action?.id
      }
    })

    await props.onCopy()
    notification.info(t`Campaign action "${type}" was successfully copied`)
    setIsCopying(false)
  }

  return (
    <Modal
      show
      onHide={onHide}
      onHidden={onHidden}
      header={t`Copy ${type} action`}
      controls={[
        <ButtonClean key={1} onClick={onHide}>
          <Trans>Cancel</Trans>
        </ButtonClean>,
        <ButtonDanger key={2} onClick={onCopy} isLoading={isCopying}>
          <Trans>Copy</Trans>
        </ButtonDanger>
      ]}
    >
      <Trans>
        Are you sure you want to copy campaign action <b>{name}</b>?
      </Trans>
    </Modal>
  )
}
